import React from 'react';
import { Navigate, Route, matchPath } from 'react-router-dom';
import studentsFamiliesComponents from '../../../../../config/semantic-components/students-families-1.0.json';
import studentsFamiliesRoutesConfig from '../../../../../config/semantic-routes/students-families.json';
import { StudentsDashboard } from '../../internal-owned/school-students-v2-1/pages/StudentsDashboard';
import {
  FamilyChildrenPage,
  FamilyDocumentsPage,
  FamilyFamilyPage,
  FamilyNotesPage,
  FamilyTuitionPage,
} from '../../internal-owned/school-students-v2-1/pages/FamilyDetail';
import { StudentsFamiliesObjectTreeProvider } from '../../internal-owned/school-students-v2-1/objectTree';
import {
  SemanticRouteAdapter,
  type SemanticComponentsConfig,
  type SemanticRoutesConfig,
} from '../SemanticRouteAdapter';

const componentsConfig = studentsFamiliesComponents as SemanticComponentsConfig;
const routesConfig = studentsFamiliesRoutesConfig as SemanticRoutesConfig;

// TODO(students-families-cutover): Keep the app-local Students/Families routes authoritative
// until live Storybook matrix parity, YSTNJ runtime route/API gates, and hosted-origin auth-pinned
// verification all pass for dashboard + children/family/tuition/documents/notes.
const COMPONENT_TO_CANONICAL_PATH: Record<string, string> = {
  'students-families-dashboard': '/school/students/dashboard',
  'students-families-children': '/school/students/family/:familyId/children',
  'students-families-family': '/school/students/family/:familyId/family',
  'students-families-tuition': '/school/students/family/:familyId/tuition',
  'students-families-documents': '/school/students/family/:familyId/documents',
  'students-families-notes': '/school/students/family/:familyId/notes',
};

const mapCanonicalStudentsPath = (pathname: string | null): string | null => {
  if (!pathname) return pathname;
  const normalized = pathname.replace(/\/+$/, '') || '/';

  if (normalized === '/school/students' || normalized === '/school/students/dashboard') {
    return '/admin/students/staff/dashboard';
  }

  const detailNoTab = matchPath({ path: '/school/students/family/:familyId', end: true }, normalized);
  if (detailNoTab?.params.familyId) {
    return `/admin/students/staff/family/${detailNoTab.params.familyId}/children`;
  }

  const tabs = ['children', 'family', 'tuition', 'documents', 'notes'] as const;
  for (const tab of tabs) {
    const detail = matchPath({ path: `/school/students/family/:familyId/${tab}`, end: true }, normalized);
    if (detail?.params.familyId) {
      return `/admin/students/staff/family/${detail.params.familyId}/${tab}`;
    }
  }

  const unknownDetail = matchPath({ path: '/school/students/family/:familyId/:tab', end: true }, normalized);
  if (unknownDetail?.params.familyId) {
    return `/admin/students/staff/family/${unknownDetail.params.familyId}/children`;
  }

  return pathname;
};

const resolveFamilyIdFromParams = (params: Record<string, string | undefined>): string | null => {
  const direct = params.familyId ?? params.applicationId ?? params.id;
  if (direct) {
    return direct;
  }

  const wildcard = params['*']?.replace(/^\/+|\/+$/g, '') ?? '';
  if (!wildcard) {
    return null;
  }

  const wildcardPath = `/${wildcard}`;

  const detail = matchPath({ path: '/family/:familyId/:tab', end: true }, wildcardPath);
  if (detail?.params.familyId) {
    return detail.params.familyId;
  }

  const detailNoTab = matchPath({ path: '/family/:familyId', end: true }, wildcardPath);
  if (detailNoTab?.params.familyId) {
    return detailNoTab.params.familyId;
  }

  return null;
};

const resolveInternalPath = (componentId: string, params: Record<string, string | undefined>): string | null => {
  if (componentId === 'students-families-dashboard') {
    return '/school/students/dashboard';
  }

  const familyId = resolveFamilyIdFromParams(params) ?? params.familyId;
  if (!familyId) {
    return null;
  }

  if (componentId === 'students-families-children') {
    return `/school/students/family/${familyId}/children`;
  }

  if (componentId === 'students-families-family') {
    return `/school/students/family/${familyId}/family`;
  }

  if (componentId === 'students-families-tuition') {
    return `/school/students/family/${familyId}/tuition`;
  }

  if (componentId === 'students-families-documents') {
    return `/school/students/family/${familyId}/documents`;
  }

  if (componentId === 'students-families-notes') {
    return `/school/students/family/${familyId}/notes`;
  }

  return COMPONENT_TO_CANONICAL_PATH[componentId] ?? null;
};

const StudentsInternalRoutes = (
  <>
    <Route index element={<Navigate to="dashboard" replace />} />
    <Route element={<StudentsFamiliesObjectTreeProvider />}>
      <Route path="dashboard" element={<StudentsDashboard />} />
      <Route path="family/:familyId" element={<Navigate to="children" replace />} />
      <Route path="family/:familyId/children" element={<FamilyChildrenPage />} />
      <Route path="family/:familyId/family" element={<FamilyFamilyPage />} />
      <Route path="family/:familyId/tuition" element={<FamilyTuitionPage />} />
      <Route path="family/:familyId/documents" element={<FamilyDocumentsPage />} />
      <Route path="family/:familyId/notes" element={<FamilyNotesPage />} />
    </Route>
    <Route path="*" element={<Navigate to="dashboard" replace />} />
  </>
);

export function StudentsFamiliesSemanticRoute() {
  return (
    <section data-extracted-from="owned:ystnj/semantic/students-families-route">
      <SemanticRouteAdapter
        moduleKey="students-families"
        componentsConfig={componentsConfig}
        routesConfig={routesConfig}
        mapIntegrationPath={mapCanonicalStudentsPath}
        strictNoFallback
        blockBridgedTarget
        internal={{
          resolvePath: resolveInternalPath,
          routes: StudentsInternalRoutes,
        }}
      />
    </section>
  );
}
